import { NoticeType } from '@dialogue/cms'
import type { ServiceId } from '../Patient/types'

export interface GetCmsFeedFailureProps {
  contentId: string
}

export interface GetCmsFeedSuccessProps {
  content: unknown
}

export interface GetCmsFeedProps {
  contentId: string
  locale?: string
}

export interface GetServiceFeaturesSuccessProps {
  serviceFeatures: {
    items: unknown[]
  }
}

export interface CmsService {
  id: string
  name: string
  service_id: ServiceId
}

export interface ServiceFilter {
  id: string
  name: string
  boolean_operator: 'AND' | 'OR'
  services: CmsService[]
}

export interface Content {
  data: unknown
  marks: unknown[]
  value: string
  nodeType: string
}

export interface LongSubtitleContent {
  data: unknown
  content: Content[]
  nodeType: string
}

export interface LongSubtitle {
  data: unknown
  content: LongSubtitleContent[]
  nodeType: string
}

export interface Media {
  id: string
  title: string
  url: string
  filename: string
  content_type: string
  description: string
}

export interface AppService {
  id: string
  name: string
  title: string
  short_subtitle: string
  icon?: Media
  tag: unknown
  action_type: string
  action_value?: string
  service_filter?: ServiceFilter
}

export interface ScopeOfPractice {
  id: string
  title: string
  slug: string
  content: string
  subtitle: string | null
}

export interface Item {
  id: string
  name: string
  title: string
  short_subtitle: string
  long_subtitle: LongSubtitle
  icon_name?: string
  tag: unknown
  show_privacy_policy: boolean
  show_emergency_services: boolean
  app_services: AppService[]
  service_filter: ServiceFilter
  scope_of_practice?: ScopeOfPractice
}

export interface Data {
  id: string
  content_categories?: unknown[]
  tags?: unknown[]
  name?: string
  items?: Item[]
}

export interface Component {
  id: string
  name: string
  mobile_component_name: string
  service_filter: ServiceFilter
  data?: Data
}

export interface Tab {
  id: string
  name: string
  title: string
  service_filter?: ServiceFilter
  component: Component
}

export interface Screen {
  id: string
  name: string
  mobile_scene_key: string
  title: string
  tab_title: string
  nav_icon_name: string
  service_filter?: ServiceFilter
  components: Component[]
  tabs: Tab[]
}

export interface GetLayoutSuccessProps {
  layout?: {
    id: string
    screens: Screen[]
  }
}

export interface FetchLayoutProps {
  contentTags: string[]
  locale?: string
}

export interface FetchCmsFeedContentProps {
  contentId: string
  locale?: string
}

export interface ServiceGroup {
  id: string
  contentType: 'appServiceGroup'
  name: string
  title: string
  subtitle: string
  tileDescription: string | null
  icon: Media | null
  appIconName: string | null
  showEmergencyBanner: boolean
  showPrivacyPolicy: boolean
  scopeOfPractice: ScopeOfPractice | null
  screenIconOverride: string | null
  displayServiceButtonCard: boolean
  // eslint-disable-next-line no-use-before-define
  services: Services
}

export interface GetServiceGroupsSuccessProps {
  serviceGroups: ServiceGroup[]
}

export enum ContentType {
  Audio = 'audio',
  Document = 'document',
  EmployeeBenefit = 'employeeBenefit',
  ExternalService = 'appExternalService',
  InternalService = 'appInternalService',
  Video = 'video',
}

interface BaseService {
  id: string
  name: string
  title: string
  subtitle: string
  icon: Media | null
  appIconName: string | null
  actionType: string
  actionValue: string | null
  browseTitle: string | null
  browseType: string | null
  ctaLabel: string
  // eslint-disable-next-line no-use-before-define
  contactInfo: ContactInfo | null
}

export interface InternalService extends BaseService {
  contentType: ContentType.InternalService
  serviceFilter: ServiceFilter | null
}

export interface ExternalService extends BaseService {
  contentType: ContentType.ExternalService
  providerTitle: string
  providerIcon: string | null
  navigationUrl: string | null
  // eslint-disable-next-line no-use-before-define
  serviceGroups: ServiceGroup[] | null
}

export interface GetOneServiceGroupParams {
  id: string
  locale: 'en' | 'fr'
}

export interface FailurePayload {
  id: string
  error: Error
}

export interface ServiceGroupByIdState {
  data: ServiceGroup | null
  error: Error | null
  loading: boolean
}

export interface ExternalServicesState {
  data: ExternalService | null
  error: Error | null
  loading: boolean
}

export interface GetExternalServiceParams {
  id: string
  include_service_groups?: boolean
  locale?: 'en' | 'fr'
}

export interface RawContactInfo {
  id: string
  content_type: 'contactInfo'
  name: string
  title: string | null
  content: string | null
  card_text: string
  card_call_to_action: string
  action_type: string | null
  screen_name: string | null
  navigation_url: string | null
}

export interface ContactInfo {
  id: string
  contentType: 'contactInfo'
  name: string
  title: string | null
  content: string | null
  cardText: string
  cardCallToAction: string
  actionType: string | null
  screenName: string | null
  navigationUrl: string | null
}

export interface RawAppInternalService {
  id: string
  content_type: ContentType.InternalService
  name: string
  title: string
  subtitle: string
  icon: Media | null
  app_icon_name: string | null
  action_type: string
  action_value: string | null
  browse_title: string | null
  browse_type: string | null
  cta_label: string
  service_filter: ServiceFilter | null
  contact_info: RawContactInfo | null
}

export interface RawAppExternalService {
  id: string
  content_type: ContentType.ExternalService
  name: string
  title: string
  subtitle: string
  icon: Media | null
  app_icon_name: string | null
  action_type: string
  action_value: string | null
  browse_title: string | null
  browse_type: string | null
  cta_label: string
  provider_title: string
  provider_icon: string | null
  contact_info: RawContactInfo | null
  navigation_url: string | null
  // eslint-disable-next-line no-use-before-define
  service_groups: RawAppServiceGroup[] | null
}

export type RawService = RawAppInternalService | RawAppExternalService

export interface RawAppServiceGroup {
  id: string
  content_type: 'appServiceGroup'
  name: string
  title: string
  subtitle: string
  tile_description: string | null
  icon: Media | null
  app_icon_name: string | null
  screen_icon_override: string | null
  show_emergency_services: boolean
  scope_of_practice: ScopeOfPractice | null
  show_privacy_policy: boolean
  display_service_button_card: boolean
  services: RawService[]
}

export type Services = (InternalService | ExternalService)[]

export type ServiceGroups = ServiceGroup[]

export interface RawServiceGroupSection {
  id: string
  name: string
  title: string | null
  items: RawAppServiceGroup[]
}

export interface ServiceGroupSection {
  name: string
  title: string | null
  serviceGroups: ServiceGroups
}

interface Tag {
  id: string
  title: string
}

export interface Audio {
  id: string
  created_at: string
  content_type: ContentType.Audio
  updated_at: string
  thumbnail: Media
  audio: Media
  title: string
  category: string
  subcategory: string
  length: number
  difficulty: string
  topic: Tag
  language: string
  service_filter: ServiceFilter | null
}

export interface Notice {
  type: NoticeType
  name: string
}
interface Profile {
  id: string
  name: string
  job_title: string
  user_id: number
  bio: string
  profile_picture: Media
}

export interface Document {
  id: string
  created_at: string
  updated_at: string
  content_type: ContentType.Document
  locale: string
  slug: string
  link: string
  title: string
  topic: Tag
  notice: Notice | null
  author: Profile[]
  content: string
  pdf_file: Media | null
  thumbnail: Media | null
  category: string | null
  subcategory: string | null
  length: number | null
  service_filter: ServiceFilter | null
}

export interface Video {
  id: string
  created_at: string
  content_type: ContentType.Video
  updated_at: string
  thumbnail: Media
  video: Media
  title: string
  category: string
  subcategory: string
  length: number
  difficulty: string
  topic: Tag
  language: string
  service_filter: ServiceFilter | null
}

export interface RawEmployeeBenefit {
  id: string
  content_type: ContentType.EmployeeBenefit
  name: string
  title: string
  url: string
  browse_type: string | null
  icon: Media | null
}

export interface EmployeeBenefit {
  id: string
  contentType: ContentType.EmployeeBenefit
  name: string
  title: string
  url: string
  browseType: string | null
  icon: Media | null
}
